<template>
  <div
    ref="footer"
    class="site-footer critical-component-clear"
  >
    <div class="site-footer__container container">
      <slot name="newsletter" />

      <!-- Mobile -->
      <div class="site-footer__accordions">
        <accordion-item
          v-for="(accordion, index) of accordions"
          :key="`${accordion.title}-${index}`"
          :index="index"
          :title="accordion.title"
          :active="activeAccordionIndex === index"
          @toggle-accordion="toggleAccordion(index)"
        >
          <template #content>
            <div
              v-if="accordion.content"
              v-html="accordion.content"
            />
            <footer-linklist
              v-else-if="accordion.linklist"
              :linklist="accordion.linklist"
            />
          </template>
        </accordion-item>

        <!-- Accessibility (mobile) -->
        <button
          class="button--reset site-footer__user-way button button--round"
          type="button"
          aria-label="Open accessibility settings"
          :style="{
            backgroundColor: userWay.bg
          }"
          @click="toggleAccessibility"
        >
          <!-- eslint-disable vue/no-bare-strings-in-template, vue/no-restricted-html-elements -->
          <img
            width="32"
            height="32"
            alt
            :src="userWay.src"
            loading="lazy"
          >
        </button>
      </div>

      <!-- Desktop -->
      <div class="site-footer__contact">
        <h2
          class="site-footer__heading"
          v-text="$string('general.site_footer.contact.heading')"
        />
        <div v-html="contactMarkup" />

        <!-- Accessibility (desktop) -->
        <button
          class="button--reset site-footer__user-way button button--round"
          type="button"
          aria-label="Open accessibility settings"
          :style="{
            backgroundColor: userWay.bg
          }"
          @click="toggleAccessibility"
        >
          <!-- eslint-disable vue/no-bare-strings-in-template, vue/no-restricted-html-elements -->
          <img
            width="32"
            height="32"
            alt
            :src="userWay.src"
            loading="lazy"
          >
        </button>
      </div>

      <template v-if="navigation?.length >= 2">
        <div
          v-for="(_, index) of 2"
          :key="navigation[index].id"
          :class="{
            'site-footer__menu--first': index === 0,
            'site-footer__menu--second': index === 1
          }"
          class="site-footer__menu"
        >
          <h2
            class="site-footer__heading"
            v-text="navigation[index].title"
          />
          <footer-linklist :linklist="navigation[index]" />
        </div>
      </template>

      <div class="site-footer__menu site-footer__menu--other">
        <div
          v-for="(list) of navigationOther"
          :key="list.handle"
        >
          <h2
            class="site-footer__heading"
            v-text="list.title"
          />
          <footer-linklist :linklist="list" />
        </div>
      </div>

      <div
        class="site-footer__copyright"
        v-text="$string('general.site_footer.copyright', {replace: {
          current_year: new Date().getFullYear()
        }})"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer (site-footer)
 * -----------------------------------------------------------------------------
 * Site footer component with accordion display of links.
 *
 * @param {Array} navigation - Footer Shopify linklist.
 *
 */

import AccordionItem from '~async/basic-accordion/accordion-item'
import FooterLinklist from '~async/site-footer-group/footer-linklist/footer-linklist'

export default {
  name: 'SiteFooter',
  components: {
    AccordionItem,
    FooterLinklist,
  },

  props: {
    navigation: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeAccordionIndex: -1,
      userWay: {
        src: 'https://cdn.userway.org/widgetapp/images/body_wh.svg',
        bg: '',
      },
    }
  },

  computed: {

    /**
     * Compute accordion items
     * @returns {[{title: String, content: String}]}
     */
    accordions() {
      return [
        {
          title: this.$string('general.site_footer.contact.heading'),
          content: this.contactMarkup,
        },
        ...this.navigation.map((nav) => ({
          title: nav.title,
          linklist: nav,
        })),
      ]
    },

    /**
     * Compute other navigation links for desktop
     * @returns {unknown[]|*[]}
     */
    navigationOther() {
      if (!this.navigation) {
        return []
      }
      return this.navigation.slice(2)
    },

    /**
     * Markup for the contact us section
     * @returns {String}
     */
    contactMarkup() {
      const strings = [
        this.$string('general.site_footer.contact.email_html', { decode: false }),
        this.$string('general.site_footer.contact.phone_html', { decode: false }),
        this.$string('general.site_footer.contact.hours_html', { decode: false }),
      ]
      const li = strings.map((str) => {
        return `
          <li class="site-footer__item-wrapper">
            <div class="site-footer__item">
              ${str}
            </div>
          </li>
        `
      })
      return `<ul>${li.join('')}</ul>`
    },
  },

  mounted() {
    window.addEventListener('resize', this.resizeHandler)
    this.resizeHandler()
    this.handleFormReturn()
    this.handleFormSubmit()
    this.getUserWayResources()
    cnvs.EventBus.emit('initialize-gorgias-buttons')
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.resizeHandler)
  },

  methods: {

    /**
     * Update the footer height css variable on resize
     */
    resizeHandler() {
      document.documentElement.style.setProperty(
        '--footer-height',
        `${this.$refs.footer.clientHeight}px`,
      )
    },

    /**
     * Toggle accordion's active state.
     * @param {Number} index - index in accordion array
     */
    toggleAccordion(index) {
      this.activeAccordionIndex = this.activeAccordionIndex === index
        ? ''
        : index
    },

    /**
     * Focus footer when customer returns to page after signing up.
     * Display error if search includes form_type=customer
     */
    handleFormReturn() {
      const email = sessionStorage.getItem('emailSignup')
      const shouldFocus = ['customer_posted=true', 'form_type=customer'].find(
        (key) => window.location.search.includes(key),
      )

      if (this.$variable('page.type') !== 'captcha') {
        sessionStorage.removeItem('emailSignup')
      }

      if (!shouldFocus || !email) {
        return
      }

      window.scrollTo(0, document.documentElement.scrollHeight)
      document.documentElement.style.scrollBehavior = ''

      /**
       * Handle success
       */
      if (shouldFocus === 'customer_posted=true' && email) {
        return
      }

      /**
       * Handle error
       */
      if (shouldFocus !== 'form_type=customer') {
        return
      }

      const note = document.querySelector('[js-newsletter="note"]')
      const formGroup = document.querySelector('[js-newsletter="form-group"]')

      if (!note || note.textContent) {
        return
      }

      note.textContent = this.$string('general.site_footer.newsletter.error_message')

      if (formGroup) {
        formGroup.classList.add(this.$class('error'))
      }
    },

    /**
     * Handle form submit
     */
    handleFormSubmit() {
      const form = document.querySelector('[js-newsletter="form"]')

      if (!form) {
        return
      }

      form.addEventListener('submit', () => {
        const inputField = document.getElementById('newsletter-field')
        sessionStorage.setItem('emailSignup', inputField.value)
        sessionStorage.setItem('customerEmail', inputField.value)
      })
    },

    /**
     * Toggle user way menu
     */
    toggleAccessibility() {
      if (!window.UserWay) {
        return
      }
      window.UserWay.widgetToggle()
    },

    /**
     * Sync accessibility button in footer img and background color with user
     * way widget
     * @param {Number} [retry] - The retry attempt to retrieve userway element
     */
    getUserWayResources(retry = 0) {
      const uw = document.getElementById('userwayAccessibilityIcon')
      if (!uw) {
        setTimeout(() => {
          if (retry === 20) {
            return
          }
          this.getUserWayResources(retry + 1)
        }, 100)
        return
      }

      const img = uw.querySelector('img.ui_w')

      this.userWay = {
        bg: uw.style.backgroundColor,
        src: img ? img.src : 'https://cdn.userway.org/widgetapp/images/body_wh.svg',
      }
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
</style>
